import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { PageHeader, Panel, LoadingState, EmptyState, ErrorState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { BookOpen, Plus, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const EMPTY = { symbol: "", side: "long", setup: "", entry: "", stop_loss: "", target: "", outcome: "open", score: 3, lesson: "", is_draft: false };

export default function Journal() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["journal"],
    queryFn: async () => (await api.get("/journal")).data,
  });

  const create = useMutation({
    mutationFn: async (p) => (await api.post("/journal", p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["journal"] }); setOpen(false); setForm(EMPTY); toast.success("Journal entry added (DEMO)"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/journal/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["journal"] }); toast.success("Entry deleted"); },
  });

  const submit = () => {
    if (!form.symbol.trim()) return toast.error("Enter a trading symbol (e.g. RELIANCE)");
    create.mutate({ ...form, symbol: form.symbol.trim(), entry: Number(form.entry || 0), stop_loss: Number(form.stop_loss || 0), target: Number(form.target || 0), score: Number(form.score) });
  };

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState label="Failed to load journal entries" onRetry={() => refetch()} />;

  const items = data?.items ?? [];
  const outcomeBadge = { win: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", loss: "text-rose-400 bg-rose-500/10 border-rose-500/30", open: "text-blue-400 bg-blue-500/10 border-blue-500/30" };

  return (
    <div>
      <PageHeader
        title="Trading Journal"
        subtitle="Log setups, outcomes, and lessons · DEMO entries"
        icon={BookOpen}
        testId="journal-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-journal-btn" className="bg-blue-600 hover:bg-blue-500 text-white h-9 text-xs"><Plus className="w-4 h-4 mr-1" /> New Entry</Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200 max-w-md">
              <DialogHeader><DialogTitle>New Journal Entry</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Symbol</Label><Input data-testid="journal-symbol" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" placeholder="Type symbol (e.g. RELIANCE)" /></div>
                <div>
                  <Label className="text-xs">Side</Label>
                  <Select value={form.side} onValueChange={(v) => setForm({ ...form, side: v })}>
                    <SelectTrigger className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200"><SelectItem value="long">Long</SelectItem><SelectItem value="short">Short</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Entry</Label><Input type="number" value={form.entry} onChange={(e) => setForm({ ...form, entry: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" /></div>
                <div><Label className="text-xs">Stop Loss</Label><Input type="number" value={form.stop_loss} onChange={(e) => setForm({ ...form, stop_loss: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" /></div>
                <div><Label className="text-xs">Target</Label><Input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" /></div>
                <div>
                  <Label className="text-xs">Outcome</Label>
                  <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                    <SelectTrigger className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200"><SelectItem value="open">Open</SelectItem><SelectItem value="win">Win</SelectItem><SelectItem value="loss">Loss</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="text-xs">Setup</Label><Input data-testid="journal-setup" value={form.setup} onChange={(e) => setForm({ ...form, setup: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" placeholder="DBR demand zone, SMA50 up" /></div>
                <div className="col-span-2"><Label className="text-xs">Lesson / Notes</Label><Textarea value={form.lesson} onChange={(e) => setForm({ ...form, lesson: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" rows={2} /></div>
                <label className="col-span-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                  <input data-testid="journal-draft" type="checkbox" checked={form.is_draft} onChange={(e) => setForm({ ...form, is_draft: e.target.checked })} className="accent-blue-500" />
                  Save as draft (allows incomplete entry/stop/target)
                </label>
              </div>
              <DialogFooter><Button data-testid="journal-submit" onClick={submit} disabled={create.isPending} className="bg-blue-600 hover:bg-blue-500 text-white">Save Entry</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <Panel><EmptyState label="No journal entries yet" icon={BookOpen} /></Panel>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="journal-list">
          {items.map((j) => (
            <Panel key={j.id} testId={`journal-entry-${j.symbol}`}>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-num font-bold text-slate-100">{j.symbol}</span>
                    <span className={cn("text-[10px] font-mono px-1.5 py-0.5 rounded border uppercase", j.side === "long" ? "text-emerald-400 border-emerald-500/30" : "text-rose-400 border-rose-500/30")}>{j.side}</span>
                    <span className={cn("text-[10px] font-mono px-1.5 py-0.5 rounded border uppercase", outcomeBadge[j.outcome])}>{j.outcome}</span>
                    {j.is_draft && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border uppercase text-slate-400 border-slate-600/40">Draft</span>}
                    {j.is_legacy_invalid && <span data-testid="legacy-flag" className="text-[10px] font-mono px-1.5 py-0.5 rounded border uppercase text-amber-400 border-amber-500/30 bg-amber-500/10">Legacy · Invalid</span>}
                    <DemoBadge />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={cn("w-3 h-3", n <= j.score ? "text-amber-400 fill-amber-400" : "text-slate-700")} />)}
                    </span>
                    <button data-testid={`delete-journal-${j.symbol}`} onClick={() => remove.mutate(j.id)} className="text-slate-600 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mb-2">{j.setup}</p>
                <div className="grid grid-cols-4 gap-2 text-xs mb-2">
                  <div><span className="text-slate-500">Entry</span><div className="font-mono-num text-blue-400">{fmtMoney(j.entry)}</div></div>
                  <div><span className="text-slate-500">SL</span><div className="font-mono-num text-rose-400">{fmtMoney(j.stop_loss)}</div></div>
                  <div><span className="text-slate-500">Target</span><div className="font-mono-num text-emerald-400">{fmtMoney(j.target)}</div></div>
                  <div><span className="text-slate-500">R:R</span><div className="font-mono-num text-slate-200">{j.reward_risk != null ? `${j.reward_risk}:1` : "—"}</div></div>
                </div>
                {j.lesson && <p className="text-[11px] text-slate-500 italic border-t border-[var(--border-subtle)] pt-2">💡 {j.lesson}</p>}
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
